// @ts-check
import { values, keyBy, forEach, map } from "lodash";

import {
  getPlannerColumns,
  getPlannerMacroAllocations,
  checkForPending,
  setGroupingsWithPlannerCells,
  setCalculatedSumTotals,
  setNotifications,
  pruneEmptyGroupings,
  buildGroupingLookup,
  buildCellLookup,
  hashGrouping,
  setGroupingsBudget,
  setGroupingsTotalCost,
  buildGroupLookup,
  buildLineItemConstraintLookups,
} from "../util";

import buildGroupings from "./buildGroupings/bySupply";
import rebuildWithExtraDemandGroupings from "./buildGroupings/rebuildWithExtraDemandGrouping";
import setGroupingsWithLineItems from "./setLineItems/demandLineItems";

const buildPlannerGroupedBySupply = ({
  rootGroup,
  data,
  insertExtraDemandGroupings = false,
  hideHiddenTeams,
  showObjectives,
  groupTypes,
  approvalReasons,
  shouldHash,
  isBudgetEnabled,
  useDeltaForBudget,
  overBudgetLimit,
  underBudgetLimit,
}) => {
  const columns = getPlannerColumns(data.allocationProjects);
  const allocationProjectLookup = keyBy(
    map(columns, (c) => c.allocationProject),
    "id"
  );
  const groupLookup = buildGroupLookup({
    macroAllocationGroups: data.macroAllocationGroups,
    descendantSupplyGroups: data.descendantSupplyGroups,
    roleAreaGroups: data.roleAreaGroups,
    relatedSupplyGroups: data.relatedSupplyGroups,
    descendantTargetGroups: data.descendantTargetGroups,
    relatedBudgetTargetGroups: data.relatedBudgetTargetGroups,
    groupTypes,
  });

  const macroAllocations = getPlannerMacroAllocations({
    macroAllocations: data.macroAllocations,
    allocationProjectLookup,
    includeNullStatus: !rootGroup.isSource,
    groupedByDemand: false,
  });

  const groupings = buildGroupings({
    rootGroup,
    groupLookup,
    hideHiddenTeams,
    showObjectives,
    groupTypes,
    isBudgetEnabled,
    allocationProjectLookup,
  });
  setGroupingsWithLineItems({
    groupings,
    groupLookup,
    macroAllocations,
    columnCount: columns.length,
    groupTypes,
    canRename: !rootGroup.isSource,
    canHide: !rootGroup.isSource,
    isBudgetEnabled,
    hideHiddenTeams,
  });
  setGroupingsWithPlannerCells({
    groupings,
    macroAllocations,
    allocationProjectLookup,
    isRequestor: !rootGroup.isSource,
    groupedByDemand: false,
    approvalReasons,
    useDeltaForBudget,
  });

  if (insertExtraDemandGroupings) {
    // extra business req: add extra hierarchy groups for demand line items.
    rebuildWithExtraDemandGroupings({
      groupings,
      showObjectives,
      groupTypes,
      isBudgetEnabled,
      allocationProjectLookup,
      groupLookup,
    });
  }

  if (isBudgetEnabled) {
    setGroupingsBudget({ groupings, allocationProjectLookup });
    setGroupingsTotalCost(groupings, overBudgetLimit, underBudgetLimit);
  }

  setCalculatedSumTotals(groupings, columns);
  setNotifications(groupings, columns);

  pruneEmptyGroupings(groupings, isBudgetEnabled);

  const columnLookup = keyBy(columns, "id");
  const groupingLookup = buildGroupingLookup(groupings, {});
  const { lineItemLookup } = buildLineItemConstraintLookups(
    values(groupingLookup),
    {}
  );
  const cellLookup = buildCellLookup(values(lineItemLookup), {});
  const isPendingSubmit = checkForPending(groupingLookup);

  // Hash groupings in case anything changed
  if (shouldHash) {
    forEach(groupings, hashGrouping);
  }

  return {
    isPendingSubmit,
    columns,
    groupings,
    // lookup objects: for when we need fast access to objects in the tree
    lookups: {
      allocationProjectLookup,
      columnLookup,
      groupLookup,
      groupingLookup,
      lineItemLookup,
      cellLookup,
      macroAllocationLookup: macroAllocations,
    },
  };
};

export default buildPlannerGroupedBySupply;
